import { useAuth } from "@context/AuthContext";
import { ROLES, FIRM_TYPES, isFirmOwner, isRM, isResource, isClient, hasAnyRole, isStaffingFirm, isEnterpriseFirm } from "@routes/permissions";

/**
 * The one hook pages should use for anything role/access related.
 *
 * Route-level "can this page render at all" is handled centrally by
 * RoleGuard + routes/permissions.js - pages should NOT re-implement that
 * (no more early-return "Access Denied" blocks in page components).
 *
 * This hook is for the OTHER kind of role logic: "given that I'm allowed
 * on this page, which parts of it does this role see" - e.g. showing an
 * extra tab to RMs, hiding a button from Resources, etc. Also covers
 * firm_type, which several pages condition on alongside role.
 *
 * Usage:
 *   const { role, isFirmOwner, isRM, hasAnyRole, ROLES } = useRole();
 *   if (isFirmOwner) { ... }
 *   if (hasAnyRole([ROLES.FIRM_OWNER, ROLES.RM])) { ... }
 *   if (isStaffingFirm) { ... }
 */
export function useRole() {
  const { user } = useAuth();

  return {
    role: user?.user_primary_role ?? null,
    ROLES,
    isFirmOwner: isFirmOwner(user),
    isRM: isRM(user),
    isResource: isResource(user),
    isClient: isClient(user),
    hasAnyRole: (roles) => hasAnyRole(user, roles),

    firmType: user?.firm_details?.firm_type ?? null,
    FIRM_TYPES,
    isStaffingFirm: isStaffingFirm(user),
    isEnterpriseFirm: isEnterpriseFirm(user),
  };
}
